// src/pages/super-admin/DrugPanels/CreateDrugPanel.tsx
import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { X } from "lucide-react";
import {
  useCreateDrugPanelMutation,
  type DrugPanelRequest,
} from "@/features/drug-panels/services/drugPanelApi";
import { useListDrugComponentsQuery } from "@/api/masterDataApi";
import { toast } from "sonner";

interface CreateDrugPanelProps {
  onClose: () => void;
  onSuccess?: (panel: any) => void;
}

export default function CreateDrugPanel({
  onClose,
  onSuccess,
}: CreateDrugPanelProps) {
  const [formData, setFormData] = useState<DrugPanelRequest>({
    panelName: "",
    panelDescription: "",
    components: [],
    aliases: [],
    status: "ACTIVE",
  });

  const [aliasInput, setAliasInput] = useState("");
  const [compSearch, setCompSearch] = useState("");

  const [createPanel, { isLoading }] = useCreateDrugPanelMutation();
  const { data: drugComponents, isLoading: compLoading } = useListDrugComponentsQuery();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.panelName || formData.components.length === 0) {
      toast.error("Please provide panel name and at least one component");
      return;
    }

    try {
      const created = await createPanel(formData).unwrap();
      toast.success("Drug panel created successfully");
      onSuccess?.(created);
      onClose();
    } catch (error) {
      console.error("Create panel error:", error);
      toast.error("Failed to create panel");
    }
  };

  const addComponent = (name: string) => {
    if (name && !formData.components.includes(name)) {
      setFormData({
        ...formData,
        components: [...formData.components, name],
      });
      setCompSearch("");
    }
  };

  const sortedComponents = useMemo(() => {
    if (!drugComponents) return [];
    return [...drugComponents].sort((a, b) => a.name.localeCompare(b.name));
  }, [drugComponents]);

  const filteredComponents = useMemo(() => {
    if (!compSearch) return sortedComponents;
    const lower = compSearch.toLowerCase();
    return sortedComponents.filter(
      (c) =>
        c.name.toLowerCase().includes(lower) ||
        c.code.toLowerCase().includes(lower)
    );
  }, [sortedComponents, compSearch]);
  const addAlias = () => {
    if (aliasInput.trim() && !formData.aliases.includes(aliasInput.trim())) {
      setFormData({
        ...formData,
        aliases: [...formData.aliases, aliasInput.trim()],
      });
      setAliasInput("");
    }
  };

  const removeAlias = (alias: string) => {
    setFormData({
      ...formData,
      aliases: formData.aliases.filter((a) => a !== alias),
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 max-h-[70vh] overflow-y-auto px-1">
      <div>
        <Label htmlFor="panelName">Panel Name *</Label>
        <Input
          id="panelName"
          placeholder="e.g., 10-Panel Drug Test"
          value={formData.panelName}
          onChange={(e) => setFormData({ ...formData, panelName: e.target.value })}
          disabled={isLoading}
          required
        />
      </div>

      <div>
        <Label htmlFor="panelDescription">Description</Label>
        <Textarea
          id="panelDescription"
          placeholder="Brief description of this panel..."
          value={formData.panelDescription}
          onChange={(e) => setFormData({ ...formData, panelDescription: e.target.value })}
          disabled={isLoading}
          rows={3}
        />
      </div>

      <div>
        <Label>Components * ({formData.components.length})</Label>
        <div className="flex flex-col gap-2 mt-2">
          <Input
            placeholder="Search components by name or code..."
            value={compSearch}
            onChange={(e) => setCompSearch(e.target.value)}
            disabled={isLoading || compLoading}
          />
          <select
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            onChange={(e) => {
              if (e.target.value) {
                addComponent(e.target.value);
                e.target.value = ""; // Reset select after adding
              }
            }}
            disabled={isLoading || compLoading}
          >
            <option value="">Select component to add...</option>
            {filteredComponents?.map((comp) => (
              <option 
                key={comp.id} 
                value={comp.name}
                disabled={formData.components.includes(comp.name)}
              >
                {comp.name} ({comp.code})
              </option>
            ))}
          </select>
        </div>
        
        {formData.components.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {formData.components.map((comp) => (
              <Badge key={comp} className="bg-blue-100 text-blue-800 flex items-center gap-1">
                {comp}
              </Badge>
            ))}
          </div>
        )}
        
        {formData.components.length === 0 && (
          <p className="text-sm text-gray-500 mt-2">No components added yet</p>
        )}
      </div>

      <div>
        <Label>Aliases (Optional)</Label>
        <div className="flex gap-2 mt-2">
          <Input
            placeholder="Enter alternative name"
            value={aliasInput}
            onChange={(e) => setAliasInput(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addAlias();
              }
            }}
            disabled={isLoading}
          />
          <Button type="button" onClick={addAlias} disabled={isLoading}>
            Add
          </Button>
        </div>
        
        {formData.aliases.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {formData.aliases.map((alias) => (
              <Badge key={alias} variant="outline" className="flex items-center gap-1">
                {alias}
                <button
                  type="button"
                  onClick={() => removeAlias(alias)}
                  className="ml-1 hover:bg-gray-200 rounded-full p-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}
      </div>

      <div>
        <Label htmlFor="status">Status</Label>
        <select
          id="status"
          value={formData.status}
          onChange={(e) =>
            setFormData({ ...formData, status: e.target.value as "ACTIVE" | "INACTIVE" })
          }
          className="w-full border rounded px-3 py-2"
          disabled={isLoading}
        >
          <option value="ACTIVE">Active</option>
          <option value="INACTIVE">Inactive</option>
        </select>
      </div>

      <div className="flex gap-2 justify-end pt-4 sticky bottom-0 bg-white border-t mt-6 py-3">
        <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={isLoading}>
          {isLoading ? "Creating..." : "Create Panel"}
        </Button>
      </div>
    </form>
  );
}
